import { useCustomTranslation } from '../../../../../hooks';
import { useState } from 'react';
import { useGraphneResourcesSoftAssignmentTaskId } from '../../../../../queryHooks/productionPlanHooks';
import { Worker } from './Worker';
import { Skeleton, Chip, Toggle } from '@nexelem/components';
import { useSelectGanttResourceArea } from '../../components/BrigadeDrawer';
import { useDelayedState } from '@nexelem/utils';
import { SearchChip } from '@nexelem/inputs';
import { TaskGanttView } from '@nexelem/graphne-api';
import { formatRequiredWorkers } from '../../utils/formatRequiredWorkers';

enum OnlyMyGroupToggle {
  MY_BRIGADE = 'true',
  ALL = 'false',
}

type AvailableWorkersProps = {
  node: TaskGanttView;
};

export const AvailableWorkers = ({ node }: AvailableWorkersProps) => {
  const { id, requiredSoftResourcesAmount } = node;
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.assignWorkerModal');
  const [toggle, setToggle] = useState<OnlyMyGroupToggle>(OnlyMyGroupToggle.MY_BRIGADE);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const { areaId, isFetched } = useSelectGanttResourceArea();
  const [search, delayedSearch, setSearch] = useDelayedState('');

  const formattedRequiredWorkers = formatRequiredWorkers(requiredSoftResourcesAmount);

  const { data, isLoading } = useGraphneResourcesSoftAssignmentTaskId(
    {
      taskId: id.toString(),
      onlyAvailable,
      myGroupId: toggle === OnlyMyGroupToggle.MY_BRIGADE ? areaId : undefined,
      name: delayedSearch ? delayedSearch : undefined,
    },
    toggle === OnlyMyGroupToggle.MY_BRIGADE ? !!(isFetched && areaId) : true,
  );

  return (
    <>
      <div className="text-s18">
        {formattedRequiredWorkers === null
          ? tWrapper('selectWorkerLessThanOne')
          : formattedRequiredWorkers === 1
            ? tWrapper('selectWorkerFromListOne', { count: formattedRequiredWorkers })
            : tWrapper('selectWorkerFromListOther', { count: formattedRequiredWorkers })}
      </div>
      <div className="my-2 flex items-center gap-2">
        <SearchChip
          placeholder={t('workers.competenceMatrix.filters.comboSearch')}
          value={search}
          onChange={setSearch}
        />
        <Toggle.Root className="self-start" value={toggle} onChange={setToggle}>
          <Toggle.Button value={OnlyMyGroupToggle.MY_BRIGADE}>{tWrapper('myBrigade')}</Toggle.Button>
          <Toggle.Button value={OnlyMyGroupToggle.ALL}>{tWrapper('all')}</Toggle.Button>
        </Toggle.Root>

        <Chip
          label={tWrapper('onlyAvailable')}
          selected={onlyAvailable}
          onClick={() => setOnlyAvailable(!onlyAvailable)}
        />
      </div>
      <div className="flex flex-col gap-1">
        {isLoading && (
          <>
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton className="h-22" key={index} />
            ))}
          </>
        )}
        {data?.map((resource) => (
          <Worker data={resource} key={resource.resource.id} node={node} />
        ))}
        {data?.length === 0 && <div className="mt-8 text-center text-s18">{tWrapper('noWorkersFound')}</div>}
      </div>
    </>
  );
};
